import { useState, type FC, type ReactNode } from 'react'
import { Stack, Group, Text, Badge, Collapse, Anchor } from '@mantine/core'
import {
    DatasetType,
    type AudienceConfiguration,
    type UserAttributesDefinition,
    type UserTransactionBehavior,
} from '@/lib/domain'
import { colorForDataset } from '@/lib/datasetColors'
import {
    attributeNarrative,
    attributeGroupLabel,
    transactionNarrative,
    userBucketLabel,
    type EffectiveKind,
    type NarrativeCondition,
    type ConditionBucket,
    type PatternBucket,
    type PatternNarrative,
    type BehaviorNarrative,
} from '../segmentNarrative'
import { IconTargetArrow } from '@tabler/icons-react'

const datasetName = (id: string, names: Record<string, string>): string =>
    names[id] ?? id

// The source dataset as the standard colour-coded chip — same treatment (and
// shared `colorForDataset`) as the cart and recommendation chips, so a dataset
// keeps one colour across the app.
const DatasetChip: FC<{ id: string; names: Record<string, string> }> = ({
    id,
    names,
}) => (
    <div>
        <Badge variant='light' color={colorForDataset(id)} size='sm'>
            {datasetName(id, names)}
        </Badge>
    </div>
)

// --- Inline filter expression (one transaction's, or one attribute set's) ----

// A soft, quiet chip for the secondary half of a condition (the part that isn't
// emphasised). Transactions chip the column; user attributes chip the value(s).
const SoftChip: FC<{ children: ReactNode }> = ({ children }) => (
    <Text
        span
        style={{
            background: 'var(--mantine-color-default-hover)',
            borderRadius: 'var(--mantine-radius-sm)',
            padding: '1px 6px',
            whiteSpace: 'nowrap',
        }}
    >
        {children}
    </Text>
)

// A muted, italic connective ("and" / "or" / "not") so the boolean structure is
// explicit but recedes behind the conditions themselves.
const Conn: FC<{ children: ReactNode }> = ({ children }) => (
    <Text span c='dimmed' fs='italic'>
        {children}
    </Text>
)

// One field condition. A field's values are OR'd (an IN match), so multiple join
// with "or". By default (transactions) the value leads as the emphasised text and
// the column trails as a soft chip. With `fieldLeads` (user attributes) it's the
// other way round — the field name leads as the emphasised text and the value(s)
// become the chip.
const Cond: FC<{
    condition: NarrativeCondition
    negate?: boolean
    fieldLeads?: boolean
}> = ({ condition, negate, fieldLeads }) => {
    const value = condition.values.join(' or ') || '—'
    return (
        <Text span style={{ whiteSpace: 'nowrap' }}>
            {negate && <Conn>not </Conn>}
            {fieldLeads ? (
                <>
                    <Text span fw={500}>
                        {condition.label}{' '}
                    </Text>
                    <SoftChip>{value}</SoftChip>
                </>
            ) : (
                <>
                    <Text span fw={500}>
                        {value}{' '}
                    </Text>
                    <SoftChip>{condition.label}</SoftChip>
                </>
            )}
        </Text>
    )
}

// A definition's field conditions as one readable expression: required fields
// AND'd, a 2+ "should" group as a parenthesised OR, "must_not" fields negated.
// Reused for a transaction pattern and for a user-attribute definition.
const ConditionExpression: FC<{
    buckets: ConditionBucket[]
    // Attribute display: field leads, value is the chip (see `Cond`).
    fieldLeads?: boolean
}> = ({ buckets, fieldLeads }) => {
    if (buckets.length === 0) {
        return (
            <Text span c='dimmed' fs='italic'>
                any purchase
            </Text>
        )
    }
    const nodes: ReactNode[] = []
    const joinAnd = (key: string) => {
        if (nodes.length > 0) nodes.push(<Conn key={`and-${key}`}> and </Conn>)
    }
    const condKey = (c: NarrativeCondition): string =>
        `${c.label}=${c.values.join(',')}`
    buckets.forEach((bucket) => {
        if (bucket.kind === 'anyOf') {
            joinAnd('any')
            const inner: ReactNode[] = []
            bucket.conditions.forEach((c) => {
                const ck = condKey(c)
                if (inner.length > 0)
                    inner.push(<Conn key={`or-${ck}`}> or </Conn>)
                inner.push(
                    <Cond
                        key={`a-${ck}`}
                        condition={c}
                        fieldLeads={fieldLeads}
                    />
                )
            })
            nodes.push(
                <Text span key='any-group'>
                    ({inner})
                </Text>
            )
        } else {
            const bucketExcluded = bucket.kind === 'excluded'
            bucket.conditions.forEach((c) => {
                const ck = `${bucket.kind}-${condKey(c)}`
                joinAnd(ck)
                nodes.push(
                    <Cond
                        key={ck}
                        condition={c}
                        negate={bucketExcluded}
                        fieldLeads={fieldLeads}
                    />
                )
            })
        }
    })
    return <Text size='sm'>{nodes}</Text>
}

// --- The two levels --------------------------------------------------------

// A stable, content-derived key for a transaction pattern (no array index).
const patternKey = (pattern: PatternNarrative): string =>
    pattern.buckets
        .map(
            (b) =>
                `${b.kind}:${b.conditions.map((c) => `${c.label}=${c.values.join(',')}`).join('&')}`
        )
        .join(';')

// A list of pattern/group buckets (required / anyOf / excluded), each a labelled,
// marker-prefixed set of filter expressions. Shared by the transaction behaviour
// view (patterns = purchases) and the user-attribute view (patterns = attribute
// groups); only the bucket-label wording differs, so it's passed in.
const PatternBucketsView: FC<{
    buckets: PatternBucket[]
    labelFor: (kind: EffectiveKind, count: number) => string
    // Attribute display: field leads, value is the chip (see `Cond`).
    fieldLeads?: boolean
}> = ({ buckets, labelFor, fieldLeads }) => (
    <Stack gap='sm' pl='sm'>
        {buckets.map((bucket) => {
            // "+" a required item, "−" an excluded one, "•" one of the any-of
            // alternatives. The all/any/none logic lives in the header label.
            const marker =
                bucket.kind === 'required'
                    ? '+'
                    : bucket.kind === 'excluded'
                      ? '−'
                      : '•'
            return (
                <div key={bucket.kind}>
                    <Text size='sm' mb={2}>
                        {labelFor(bucket.kind, bucket.patterns.length)}:
                    </Text>
                    <Stack gap={4} pl={4}>
                        {bucket.patterns.map((pattern) => (
                            <Group
                                key={patternKey(pattern)}
                                gap={8}
                                wrap='nowrap'
                                align='flex-start'
                            >
                                <Text size='sm' c='dimmed' fw={600} lh={1.55}>
                                    {marker}
                                </Text>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <ConditionExpression
                                        buckets={pattern.buckets}
                                        fieldLeads={fieldLeads}
                                    />
                                </div>
                            </Group>
                        ))}
                    </Stack>
                </div>
            )
        })}
    </Stack>
)

// One user behaviour: the agent's note (always shown), then the pattern buckets —
// which purchases the user must / at-least-one-of / must-not have made — collapsed
// behind the section-level toggle (`opened` is owned by `SegmentMakeup`).
const BehaviorView: FC<{ behavior: BehaviorNarrative; opened: boolean }> = ({
    behavior,
    opened,
}) => (
    <Stack gap='xs'>
        {behavior.description && (
            <Text size='sm' c='dimmed'>
                {behavior.description}
            </Text>
        )}
        <Collapse in={opened}>
            <PatternBucketsView
                buckets={behavior.patternBuckets}
                labelFor={userBucketLabel}
            />
        </Collapse>
    </Stack>
)

const behaviorKey = (behavior: BehaviorNarrative): string =>
    behavior.description ||
    behavior.patternBuckets
        .map((pb) => `${pb.kind}:${pb.patterns.map(patternKey).join('|')}`)
        .join(';') ||
    'behavior'

// All behaviors for a transaction (or crossed) config. Behaviors are OR'd — a
// person matches if they fit ANY profile. Reused on its own (transaction
// segments) and as the transaction side of a crossed segment.
export const TransactionNarrativeView: FC<{
    behaviors: UserTransactionBehavior[] | undefined
    opened: boolean
}> = ({ behaviors, opened }) => {
    const narratives = transactionNarrative(behaviors)
    return (
        <Stack gap='md'>
            {narratives.length === 0 ? (
                <Text size='sm' c='dimmed' fs='italic'>
                    No transaction behaviors specified.
                </Text>
            ) : (
                <>
                    {narratives.length > 1 && (
                        <Text size='sm' c='dimmed'>
                            Reach people who match any of these profiles:
                        </Text>
                    )}
                    {narratives.map((narrative, index) => (
                        <div key={behaviorKey(narrative)}>
                            <BehaviorView
                                behavior={narrative}
                                opened={opened}
                            />
                            {index < narratives.length - 1 && (
                                <Text size='xs' c='dimmed' fw={500} my='sm'>
                                    or
                                </Text>
                            )}
                        </div>
                    ))}
                </>
            )}
        </Stack>
    )
}

// Readable narrative for a user-attribute definition (the two-level
// `UserAttributesDefinition` — groups of attribute filters). Reused on its own and
// as the attribute side of a crossed segment. The common case is a single group,
// rendered inline as one expression; multiple groups show the group-level AND /
// OR / NOT structure with labels, mirroring the transaction behaviour view.
export const AttributeNarrativeView: FC<{
    definition: UserAttributesDefinition | undefined
    opened: boolean
}> = ({ definition, opened }) => {
    const buckets = attributeNarrative(definition)
    if (buckets.length === 0) {
        return (
            <Text size='sm' c='dimmed' fs='italic'>
                No conditions specified.
            </Text>
        )
    }
    // A lone required group carries no group-level structure, so drop the "Match:"
    // header + marker and just show its conditions inline (the clean default);
    // multiple groups show the group-level AND / OR / NOT structure.
    const body =
        buckets.length === 1 &&
        buckets[0].kind === 'required' &&
        buckets[0].patterns.length === 1 ? (
            <ConditionExpression
                buckets={buckets[0].patterns[0].buckets}
                fieldLeads
            />
        ) : (
            <PatternBucketsView
                buckets={buckets}
                labelFor={attributeGroupLabel}
                fieldLeads
            />
        )
    // The filter detail collapses behind the section-level toggle owned by
    // `SegmentMakeup`.
    return <Collapse in={opened}>{body}</Collapse>
}

// The audience makeup for any config type. A crossed segment reuses the
// transaction and user-attribute views above, each labelled by its dataset and
// joined by "and also".
const SegmentNarrative: FC<{
    config: AudienceConfiguration
    names: Record<string, string>
    opened: boolean
}> = ({ config, names, opened }) => {
    if (config.dataset_type === DatasetType.USER_ATTRIBUTE) {
        return (
            <Stack gap='xs'>
                <DatasetChip id={config.dataset_id} names={names} />
                {/* For a standalone attribute audience the whole segment IS the
                    attribute selection, so its config description is the note for
                    this section — the analogue of the crossed attribute-side note. */}
                {config.description && (
                    <Text size='sm' c='dimmed'>
                        {config.description}
                    </Text>
                )}
                <AttributeNarrativeView
                    definition={config.users_with_attributes}
                    opened={opened}
                />
            </Stack>
        )
    }
    if (config.dataset_type === DatasetType.TRANSACTION) {
        return (
            <Stack gap='sm'>
                <DatasetChip id={config.dataset_id} names={names} />
                <TransactionNarrativeView
                    behaviors={config.users_with_transactions}
                    opened={opened}
                />
            </Stack>
        )
    }
    return (
        <Stack gap='md'>
            <Stack gap='sm'>
                <DatasetChip id={config.dataset_id} names={names} />
                <TransactionNarrativeView
                    behaviors={config.users_with_transactions}
                    opened={opened}
                />
            </Stack>
            <Text size='xs' c='dimmed' fw={500}>
                Filtered by
            </Text>
            <Stack gap='xs'>
                <DatasetChip
                    id={config.users_with_attributes_dataset_id}
                    names={names}
                />
                {/* The cross's attribute-side note — the analogue of the per-
                    behaviour description on the transaction side above. */}
                {config.users_with_attributes_description && (
                    <Text size='sm' c='dimmed'>
                        {config.users_with_attributes_description}
                    </Text>
                )}
                <AttributeNarrativeView
                    definition={config.users_with_attributes}
                    opened={opened}
                />
            </Stack>
        </Stack>
    )
}

// Whether a config has any collapsible filter detail — i.e. anything the
// section-level "Show details" toggle would actually reveal. Descriptions and
// dataset chips stay visible regardless, so they don't count.
const hasCollapsibleDetail = (config: AudienceConfiguration): boolean => {
    const hasTxn = (b: UserTransactionBehavior[] | undefined): boolean =>
        transactionNarrative(b).some((n) => n.patternBuckets.length > 0)
    const hasAttr = (d: UserAttributesDefinition | undefined): boolean =>
        attributeNarrative(d).length > 0
    switch (config.dataset_type) {
        case DatasetType.USER_ATTRIBUTE:
            return hasAttr(config.users_with_attributes)
        case DatasetType.TRANSACTION:
            return hasTxn(config.users_with_transactions)
        case DatasetType.CROSSED:
            return (
                hasTxn(config.users_with_transactions) ||
                hasAttr(config.users_with_attributes)
            )
    }
}

// The Makeup tab's audience-definition section: the source dataset chip + the
// filter breakdown. One section-level "Show details" toggle controls every
// collapse inside (all behaviours + the attribute view). The reach / CPM /
// description band lives in the shared header above the tabs (see SegmentDetail),
// so it isn't repeated here.
export const SegmentMakeup: FC<{
    config: AudienceConfiguration
    names: Record<string, string>
}> = ({ config, names }) => {
    const [opened, setOpened] = useState(false)
    return (
        <Stack gap='sm'>
            <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                <IconTargetArrow
                    size={14}
                    style={{ verticalAlign: 'text-bottom' }}
                />{' '}
                Who are we targeting?
            </Text>
            <SegmentNarrative config={config} names={names} opened={opened} />
            {hasCollapsibleDetail(config) && (
                <Anchor
                    component='button'
                    type='button'
                    size='xs'
                    fw={600}
                    onClick={() => setOpened((o) => !o)}
                    style={{ alignSelf: 'flex-end' }}
                >
                    {opened ? 'Hide details' : 'Show details'}
                </Anchor>
            )}
        </Stack>
    )
}
